import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Camera, Paperclip, CalendarClock } from "lucide-react";
import SectionCard from "./SectionCard";
import RecordFormModal from "./RecordFormModal";
import { api, photoUrl, formatApiError } from "../../api";
import { fmtDate, fmtKm, fmtTry } from "../../lib/labels";

const emptyBakim = () => ({ date: new Date().toISOString().slice(0, 10), title: "", part: "", servis: "", cost: "", km: "", description: "", photos: [] });

const addYear = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

export default function MaintenanceSection({ vehicle, onSaved }) {
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const testid = "section-bakim";

  const records = (vehicle.history || [])
    .filter((h) => h.type === "servis")
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  const last = records[0];
  const totalCost = records.reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const nextDate = last && last.date ? addYear(last.date) : null;
  const nextKm = last && last.km != null ? Number(last.km) + 10000 : null;
  const nextDays = nextDate ? Math.ceil((new Date(`${nextDate}T00:00:00`) - new Date(new Date().toDateString())) / 86400000) : null;
  const files = records.flatMap((r) => (r.photos || []).map((p) => ({ p, date: r.date, title: r.title, rid: r.id })));

  const openAdd = () => setModal({ record: null, data: emptyBakim() });
  const openEdit = (r) => setModal({
    record: r,
    data: { date: r.date, title: r.title, part: r.part || "", servis: r.servis || "", cost: r.cost ?? "", km: r.km ?? "", description: r.description || "", photos: r.photos || [] },
  });

  const remove = async (r) => {
    if (!window.confirm("Bu kayıt silinsin mi?")) return;
    try {
      const { data } = await api.delete(`/my-vehicles/${vehicle.id}/records/${r.id}`);
      onSaved(data);
      toast.success("Kayıt silindi");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Dosya 5 MB'ı aşamaz"); return; }
    if (!last) { toast.error("Önce bir bakım kaydı ekleyin; dosyalar kayıtlara eklenir"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/upload", fd);
      const payload = {
        type: "servis", date: last.date, title: last.title, part: last.part || "", servis: last.servis || "",
        cost: last.cost ?? null, km: last.km ?? null, description: last.description || "",
        photos: [...(last.photos || []), up.path],
      };
      const { data } = await api.put(`/my-vehicles/${vehicle.id}/records/${last.id}`, payload);
      onSaved(data);
      toast.success("Dosya son bakım kaydına eklendi");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const addBtn = (
    <button data-testid={`${testid}-add-button`} onClick={openAdd}
      className="flex items-center gap-1.5 text-sm font-bold px-5 py-2.5 rounded-xl bg-cyan-400 text-slate-950 hover:bg-cyan-300 transition-colors">
      <Plus className="w-4 h-4" /> Kayıt Ekle
    </button>
  );

  return (
    <SectionCard testid={testid} title="Bakım Geçmişi" desc="Servis ve bakım kayıtlarını fatura/fotoğraf ile birlikte ekleyin." Icon={CalendarClock} hideActions>
      <div className="flex items-center justify-between flex-wrap gap-3">
        {addBtn}
      </div>

      {records.length === 0 ? (
        <div data-testid={`${testid}-empty`} className="mt-5 rounded-xl border border-dashed border-white/15 p-10 text-center">
          <CalendarClock className="w-9 h-9 text-slate-600 mx-auto" />
          <p className="mt-3 text-base font-semibold text-slate-300">Henüz kayıt bulunmuyor</p>
          <p className="mt-1 text-sm text-slate-500">İlk bakım kaydınızı ekleyerek aracınızın bakım geçmişini oluşturun.</p>
          <div className="mt-4 flex justify-center">{addBtn}</div>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="bg-slate-950/70 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="px-3.5 py-3">Tarih</th>
                  <th className="px-3.5 py-3">KM</th>
                  <th className="px-3.5 py-3">Bakım Türü</th>
                  <th className="px-3.5 py-3">Servis</th>
                  <th className="px-3.5 py-3">Yapılan İşlemler</th>
                  <th className="px-3.5 py-3">Maliyet</th>
                  <th className="px-3.5 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} data-testid={`${testid}-row-${r.id}`} className="border-t border-white/5 align-top">
                    <td className="px-3.5 py-3 text-sm font-semibold text-slate-100 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-3.5 py-3 text-sm text-slate-300 whitespace-nowrap">{r.km != null ? fmtKm(r.km) : "—"}</td>
                    <td className="px-3.5 py-3 text-sm font-bold text-slate-100">{r.title}{r.part ? <span className="block text-xs font-medium text-slate-400">Parça: {r.part}</span> : null}</td>
                    <td className="px-3.5 py-3 text-sm text-slate-300">{r.servis || "—"}</td>
                    <td className="px-3.5 py-3 text-sm text-slate-300 max-w-[220px]">{r.description || "—"}</td>
                    <td className="px-3.5 py-3 text-sm font-bold text-cyan-300 whitespace-nowrap">{fmtTry(r.cost) || "—"}</td>
                    <td className="px-3.5 py-3">
                      <div className="flex gap-2 justify-end">
                        <button data-testid={`${testid}-edit-${r.id}`} onClick={() => openEdit(r)}
                          className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button data-testid={`${testid}-delete-${r.id}`} onClick={() => remove(r)}
                          className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/40 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-4">
            <div data-testid="bakim-ozeti" className="rounded-xl border border-white/10 bg-slate-950/50 p-4">
              <h4 className="text-sm font-bold text-white uppercase tracking-wider">Bakım Özeti</h4>
              <dl className="mt-3 space-y-2.5 text-sm">
                <div className="flex justify-between gap-2"><dt className="text-slate-400">Toplam bakım kaydı</dt><dd data-testid="ozet-toplam" className="font-bold text-slate-100">{records.length}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-400">Son bakım tarihi</dt><dd data-testid="ozet-son-tarih" className="font-bold text-slate-100">{last ? fmtDate(last.date) : "—"}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-400">Son bakım KM'si</dt><dd data-testid="ozet-son-km" className="font-bold text-slate-100">{last && last.km != null ? fmtKm(last.km) : "—"}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-400">Sonraki bakım</dt><dd data-testid="ozet-sonraki" className="font-bold text-slate-100 text-right">{nextDate ? `${fmtDate(nextDate)}${nextKm ? ` / ${fmtKm(nextKm)}` : ""}` : "—"}<span className="block text-[10px] font-medium text-slate-500">tahmini (1 yıl / 10.000 km)</span></dd></div>
                <div className="flex justify-between gap-2 border-t border-white/5 pt-2.5"><dt className="text-slate-400">Toplam bakım maliyeti</dt><dd data-testid="ozet-maliyet" className="font-extrabold text-cyan-300">{fmtTry(totalCost) || "—"}</dd></div>
              </dl>
            </div>

            <div data-testid="bakim-yaklasan" className="rounded-xl border border-white/10 bg-slate-950/50 p-4">
              <h4 className="text-sm font-bold text-white uppercase tracking-wider">Yaklaşan Bakım Zamanı</h4>
              {nextDays === null ? (
                <p className="mt-2 text-sm text-slate-500">Kayıt eklenince tahmini bakım zamanı burada görünür.</p>
              ) : nextDays < 0 ? (
                <p className="mt-2 text-sm font-bold text-red-300">Tahmini bakım zamanı geçti ({fmtDate(nextDate)})</p>
              ) : nextDays <= 30 ? (
                <p className="mt-2 text-sm font-bold text-amber-300">{nextDays} gün kaldı ({fmtDate(nextDate)})</p>
              ) : (
                <p className="mt-2 text-sm font-bold text-emerald-300">{nextDays} gün kaldı ({fmtDate(nextDate)})</p>
              )}
            </div>

            <div data-testid="bakim-dosyalar" className="rounded-xl border border-white/10 bg-slate-950/50 p-4">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-bold text-white uppercase tracking-wider">Ek Dosyalar</h4>
                <input ref={fileRef} type="file" className="hidden" data-testid="bakim-dosya-input" onChange={addFile} />
                <button data-testid="bakim-dosya-ekle" onClick={() => fileRef.current?.click()} disabled={busy}
                  className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
                  <Paperclip className="w-3.5 h-3.5" /> {busy ? "Yükleniyor..." : "Dosya Ekle"}
                </button>
              </div>
              {files.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">Fatura, servis belgesi veya bakım fotoğrafı ekleyin.</p>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  {files.map((f) => (
                    <a key={`${f.rid}-${f.p}`} href={photoUrl(f.p)} target="_blank" rel="noreferrer" data-testid={`bakim-dosya-${f.rid}`}>
                      <img src={photoUrl(f.p)} alt={f.title || "Ek dosya"} className="w-16 h-12 object-cover rounded-lg border border-white/10 hover:border-cyan-400/50 transition-colors" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {modal && (
        <RecordFormModal vehicle={vehicle} kind="bakim" modal={modal} setModal={setModal} onSaved={onSaved} testid={testid} />
      )}
    </SectionCard>
  );
}
